import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaHistory } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { discoveryService } from '../../services/apiService';
import { useVehicle } from '../../contexts/VehicleContext';

export default function SubSystemCategories() {
  const { subSystem } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedVehicle } = useVehicle();

  const vehicleId =
    searchParams.get('vehicleId') ||
    selectedVehicle?._id ||
    selectedVehicle?.id ||
    '';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!subSystem) return;
    setLoading(true);
    try {
      const res = await discoveryService.browseSubSystemCategories(
        decodeURIComponent(subSystem),
        vehicleId || undefined
      );
      setData(res?.data || res);
    } catch (err) {
      toast.error(err.message || 'Could not load categories');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [subSystem, vehicleId]);

  useEffect(() => {
    load();
  }, [load]);

  const categories = data?.categories || data?.partCategories || [];

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <FaArrowLeft /> Back
        </button>

        <h1 className="text-2xl font-bold text-slate-900 mb-1">{decodeURIComponent(subSystem || '')}</h1>
        <p className="text-sm text-slate-600 mb-6">Select a part category</p>

        {data?.requiresVehicle && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6 text-sm text-amber-900">
            {data.message}
          </div>
        )}

        {loading ? (
          <p className="text-center text-slate-500 py-12">Loading…</p>
        ) : categories.length === 0 ? (
          <p className="text-center text-slate-500 py-12">No parts found for this sub-system.</p>
        ) : (
          <ul className="space-y-2">
            {categories.map((cat) => {
              const label = cat.partCategory || cat.partName;
              const nodeId = cat.catalogNodeId;
              const qs = vehicleId ? `?vehicleId=${vehicleId}` : '';
              return (
                <li key={nodeId || label}>
                  <Link
                    to={`/catalog/node/${nodeId}${qs}`}
                    className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 hover:border-indigo-300 transition"
                  >
                    <span className="font-medium text-slate-900">{label}</span>
                    <span className="flex items-center gap-2 text-xs text-slate-500">
                      {(cat.previouslyOrdered || cat.orderedBefore) && (
                        <span className="inline-flex items-center gap-1 text-indigo-600 font-semibold">
                          <FaHistory /> Ordered before
                        </span>
                      )}
                      {cat.hasStock || (cat.stockCount ?? cat.activeListingCount) > 0 ? (
                        <span className="text-emerald-600 font-semibold">In stock</span>
                      ) : (
                        <span>No stock</span>
                      )}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
